import type { DiscordAccount, DiscordCodeInfo, MapVoiceSession, TalkSpan, TeamDiscord, VoiceFileInfo, VoiceFileUpdate, VoiceSessionInfo, VoiceTrack } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.ts";
import { matchKeys } from "./demo-queries.ts";

const keys = {
  me: ["discord", "me"] as const,
  code: (code: string) => ["discord", "code", code] as const,
  team: (id: string) => ["discord", "team", id] as const,
  sessions: (id: string) => ["discord", "sessions", id] as const,
  mapSessions: (demoId: string) => ["discord", "map-sessions", demoId] as const,
  talk: (trackId: string) => ["discord", "talk", trackId] as const,
};

/** My linked Discord account, if any. */
export const useMyDiscord = () => useQuery({ queryKey: keys.me, queryFn: () => api<DiscordAccount | null>("GET", "/me/discord") });

/** What a link from the bot is for. */
export const useDiscordCode = (code: string) =>
  useQuery({ queryKey: keys.code(code), queryFn: () => api<DiscordCodeInfo>("GET", `/discord/codes/${encodeURIComponent(code)}`), enabled: !!code, retry: false });

export function useLinkDiscord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; consent: boolean }) => api<DiscordAccount>("POST", "/discord/link", input),
    onSuccess: (account) => {
      qc.setQueryData(keys.me, account);
      void qc.invalidateQueries({ queryKey: ["discord", "team"] });
    },
  });
}

export function useDiscordConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consent: boolean) => api<DiscordAccount>("PATCH", "/me/discord", { consent }),
    onSuccess: (account) => {
      qc.setQueryData(keys.me, account);
      void qc.invalidateQueries({ queryKey: ["discord", "team"] });
    },
  });
}

export function useUnlinkDiscord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("DELETE", "/me/discord"),
    onSuccess: () => {
      qc.setQueryData(keys.me, null);
      void qc.invalidateQueries({ queryKey: ["discord", "team"] });
    },
  });
}

export const useLinkDiscordTeam = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; teamId: string }) => api<{ ok: true }>("POST", "/discord/team-link", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["discord", "team"] }),
  });
};

export const useTeamDiscord = (teamId: string) => useQuery({ queryKey: keys.team(teamId), queryFn: () => api<TeamDiscord>("GET", `/teams/${teamId}/discord`) });

export function useUnlinkDiscordTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("DELETE", `/teams/${teamId}/discord`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.team(teamId) }),
  });
}

export const useVoiceSessions = (teamId: string) => useQuery({ queryKey: keys.sessions(teamId), queryFn: () => api<VoiceSessionInfo[]>("GET", `/teams/${teamId}/voice-sessions`) });

/** A recording's track, for the ▶ of the Recordings drawer (0.24.0). */
export const sessionTrackUrl = (sessionId: string, trackId: string) => `/api/voice-sessions/${sessionId}/tracks/${trackId}/audio`;

/** When the player talked, for tracks the list has no answer for yet (recorded before 0.24.0). */
export const useTrackTalk = (sessionId: string, trackId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.talk(trackId), queryFn: () => api<TalkSpan[]>("GET", `/voice-sessions/${sessionId}/tracks/${trackId}/talk`), enabled, staleTime: Infinity });

/**
 * Puts a recording on a map from the Recordings tab ("Use on Map 2"); `replace` takes
 * the map's other voice source off first, after the player said yes (409 other_source).
 */
export function useUseRecording(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ demoId, sessionId, replace = false }: { demoId: string; sessionId: string; replace?: boolean }) =>
      api<VoiceTrack[]>("POST", `/demos/${demoId}/voices/from-session`, { sessionId, replace }),
    onSuccess: (_tracks, { demoId }) => {
      void qc.invalidateQueries({ queryKey: keys.sessions(teamId) });
      void qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) });
      void qc.invalidateQueries({ queryKey: keys.mapSessions(demoId) });
    },
  });
}

export function useDeleteVoiceSession(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/voice-sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.sessions(teamId) }),
  });
}

/** The team's recordings made around this map's match. */
export const useMapVoiceSessions = (demoId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.mapSessions(demoId), queryFn: () => api<MapVoiceSession[]>("GET", `/demos/${demoId}/voice-sessions`), enabled });

export function useAddVoiceSession(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `replace`: the map's other voice source (files, or another recording) leaves first (0.24.0).
    mutationFn: (input: string | { sessionId: string; replace?: boolean }) =>
      api<VoiceTrack[]>("POST", `/demos/${demoId}/voices/from-session`, typeof input === "string" ? { sessionId: input } : input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) });
      void qc.invalidateQueries({ queryKey: keys.mapSessions(demoId) });
      void qc.invalidateQueries({ queryKey: ["discord", "sessions"] });
      void qc.invalidateQueries({ queryKey: ["voice-files"] });
    },
  });
}

/** Takes a bot recording off a map: every track that came from it (0.24.0). */
export function useRemoveVoiceSession(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api<void>("DELETE", `/demos/${demoId}/voices/session/${sessionId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) });
      void qc.invalidateQueries({ queryKey: keys.mapSessions(demoId) });
      void qc.invalidateQueries({ queryKey: ["discord", "sessions"] });
      void qc.invalidateQueries({ queryKey: ["voice-files"] });
    },
  });
}

// ---------------------------------------------------------------- the team's uploaded files (0.25.0)

const fileKeys = { list: (teamId: string) => ["voice-files", teamId] as const };

export const voiceFileUrl = (id: string) => `/api/voice-files/${id}/audio`;

export const useVoiceFiles = (teamId: string | null) =>
  useQuery({ queryKey: fileKeys.list(teamId ?? ""), queryFn: () => api<VoiceFileInfo[]>("GET", `/teams/${teamId}/voice-files`), enabled: !!teamId });

/** Renames a file, or keeps the length a browser found (once). Its map's track follows. */
export function useUpdateVoiceFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: VoiceFileUpdate & { id: string }) => api<void>("PATCH", `/voice-files/${id}`, input),
    onSuccess: (_r, input) => {
      void qc.invalidateQueries({ queryKey: ["voice-files"] });
      if (input.label !== undefined) void qc.invalidateQueries({ queryKey: ["matches", "voices"] });
    },
  });
}

/** Deletes a file for good: off its map too. */
export function useDeleteVoiceFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/voice-files/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["voice-files"] });
      void qc.invalidateQueries({ queryKey: ["matches", "voices"] });
    },
  });
}

/** Puts a kept file back on a map; `replace` takes a bot recording off first. */
export function useUseVoiceFile(demoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, replace = false }: { fileId: string; replace?: boolean }) => api<VoiceTrack>("POST", `/demos/${demoId}/voices/from-file`, { fileId, replace }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: matchKeys.voices(demoId) });
      void qc.invalidateQueries({ queryKey: keys.mapSessions(demoId) });
      void qc.invalidateQueries({ queryKey: ["voice-files"] });
      void qc.invalidateQueries({ queryKey: ["discord", "sessions"] });
    },
  });
}
